import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { authApi } from "../../api/auth";
import { ApiError, errorMessage } from "../../api/client";
import { AuthLayout } from "../../components/AuthLayout";
import { Button, Field, TextInput, ErrorText } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import { BackupCodeStep, BackupCodesReveal, EnrollStep, TotpStep } from "./AdminMfaSteps";
import { PasskeyButton, UsePasswordLink } from "./PasskeySignIn";
import { PasswordChangeStep } from "./PasswordChangeStep";

export type AdminStep = "credentials" | "pw" | "password" | "totp" | "backup" | "enroll" | "codes";

export default function AdminLogin() {
  const { session, refresh, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<AdminStep>("credentials");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [full, setFull] = useState(false);
  const [remember, setRemember] = useState(false);
  const rememberProps = full ? { remember, setRemember } : {};
  const toPassword = () => { setFull(false); setStep("pw"); };

  useEffect(() => {
    if (loading || step === "codes") return;
    if (session?.authenticated && session.role === "ADMIN") navigate("/admin", { replace: true });
    else if (session?.stage === "PENDING_MFA" && step === "credentials") setStep("totp");
    else if (session?.stage === "PENDING_ENROLLMENT" && step === "credentials") setStep("enroll");
    else if (session?.stage === "PENDING_PASSWORD_CHANGE" && step === "credentials") setStep("password");
  }, [loading, session, step, navigate]);

  const handleStepError = (err: unknown): string => {
    if (err instanceof ApiError && (err.code === "INVALID_AUTH_STEP" || err.code === "UNAUTHENTICATED")) {
      toast.error(err.message);
      setStep("credentials");
      void refresh();
    }
    return errorMessage(err);
  };

  const finish = async () => {
    await refresh();
    navigate("/admin", { replace: true });
  };

  const titles: Record<AdminStep, [string, string]> = {
    credentials: ["Manage Time", "Sign in with a passkey, or your password and authenticator app."],
    pw: ["Manage Time", "Enter your password, then your authenticator code."],
    password: ["Choose your password", "Your temporary password works once. Set your own before enrolling 2FA."],
    totp: ["Two-factor check", "Enter the 6-digit code from your authenticator app."],
    backup: ["Use a backup code", "Each backup code works exactly once."],
    enroll: ["Set up 2FA", "Two-factor authentication is required before admin access."],
    codes: ["Save backup codes", "These are shown once. Store them somewhere safe."],
  };

  return (
    <AuthLayout backdrop="lead" testId="admin-login-page" eyebrow="Crew Leads" title={titles[step][0]} subtitle={titles[step][1]}>
      {step === "credentials" && <EmailStep email={email} setEmail={setEmail} onNext={(next) => setStep(next)} onDone={finish} />}
      {step === "pw" && <CredentialsStep email={email} onBack={() => setStep("credentials")} onDone={finish}
        onNext={(next) => { setFull(true); setStep(next); }} />}
      {step === "password" && <PasswordChangeStep onNext={(next) => setStep(next)} onError={handleStepError} />}
      {step === "totp" && <TotpStep {...rememberProps} onDone={finish} onError={handleStepError} onUseBackup={() => setStep("backup")} />}
      {step === "backup" && <BackupCodeStep {...rememberProps} onDone={finish} onError={handleStepError} onBack={() => setStep("totp")} />}
      {(step === "totp" || step === "backup") && !full && <UsePasswordLink onClick={toPassword} />}
      {step === "enroll" && (
        <EnrollStep
          onError={handleStepError}
          onDone={(codes) => {
            setBackupCodes(codes);
            setStep("codes");
          }}
        />
      )}
      {step === "codes" && <BackupCodesReveal codes={backupCodes} onContinue={finish} />}
    </AuthLayout>
  );
}

type Next = (s: AdminStep) => void;
const stepFor = (n: string): AdminStep => (n === "MFA_VERIFY" ? "totp" : n === "PASSWORD_CHANGE" ? "password" : "enroll");

function EmailStep({ email, setEmail, onNext, onDone }: { email: string; setEmail: (v: string) => void; onNext: Next; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await authApi.adminStart(email);
      onNext(res.next_step === "MFA_VERIFY" ? "totp" : "pw");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-6">
      <form onSubmit={submit} className="space-y-6" data-testid="admin-email-form">
        <Field label="Email" htmlFor="admin-email">
          <TextInput id="admin-email" type="email" autoComplete="username webauthn" required value={email}
            onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input" />
        </Field>
        <ErrorText message={error} testId="admin-login-error" />
        <Button type="submit" loading={busy} disabled={!email.includes("@")} className="w-full" data-testid="admin-login-email-continue">Continue</Button>
      </form>
      <PasskeyButton onResult={(n) => (n === "DONE" ? onDone() : onNext(stepFor(n)))} onUsePassword={() => onNext("pw")} />
    </div>
  );
}

function CredentialsStep({ email, onNext, onBack, onDone }: { email: string; onNext: Next; onBack: () => void; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await authApi.adminLogin(email, password);
      onNext(stepFor(res.next_step));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={submit} className="space-y-6" data-testid="admin-credentials-form">
        <p className="flex items-center justify-between gap-3 rounded-xl border border-white/10 px-3 py-2 text-sm">
          <span className="truncate text-zinc-200" data-testid="admin-login-email-display">{email}</span>
          <button type="button" onClick={onBack} className="shrink-0 text-xs font-semibold text-signal hover:text-white" data-testid="admin-login-change-email">Change</button>
        </p>
        <input type="email" value={email} readOnly autoComplete="username" hidden />
        <Field label="Password" htmlFor="admin-password">
          <TextInput id="admin-password" type="password" autoComplete="current-password" required autoFocus value={password}
            onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password-input" />
        </Field>
        <ErrorText message={error} testId="admin-login-error" />
        <Button type="submit" loading={busy} className="w-full" data-testid="admin-login-submit-button">
          Continue
        </Button>
        <p className="text-xs text-zinc-500">5 failed attempts lock the account for 15 minutes.</p>
      </form>
      <PasskeyButton onResult={(n) => (n === "DONE" ? onDone() : onNext(stepFor(n)))} variant="ghost" />
    </div>
  );
}
